import renderSlideIdCell, { renderSlideIdsCell } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import {
  renderCaseMarkersCell,
  renderSlideMarkersCell,
} from 'components/atoms/DataGridRenderers/SlideMarkersCellRenderer';
import { concat, find, map, size, some, toNumber, toString } from 'es-toolkit/compat';
import { Procedure } from 'interfaces/procedure';
import { MULTIPLEX_STAIN_ID, StainType } from 'interfaces/stainType';
import { formatDateTimeForUser } from 'utils/dateTime';
import { formatNumber, formatSlideResolution, humanize } from 'utils/helpers';
import { DisplayedField, FieldOption } from '../../genericFields';
import { getDisplayNameFromEnumValue } from '../../genericFields/formatFieldValues';
import { getProcedureSlideFieldValues, ProceduresFieldsContext } from './helpers';

export const originalFileNameField: DisplayedField<Procedure, Array<string | null | undefined>> = {
  filterType: 'multiText',
  dataCategory: 'metadata',
  dataKey: 'originalFileName',
  label: 'Original File Name(s)',
  columnWidth: { width: 200 },
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'originalFileName'),
  isFieldOfObjectInArray: true,
  objectArrayInnerPath: 'originalFileName',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
};

export const formatField: DisplayedField<Procedure, Array<string | null | undefined>> = {
  filterType: 'multiText', // TODO: change to multiSelect once converted to enum
  dataCategory: 'metadata',
  dataKey: 'format',
  label: 'Format',
  columnWidth: { width: 200 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'format'),
  objectArrayInnerPath: 'format',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
};

export const slideIdField: DisplayedField<Procedure, string[]> = {
  filterType: 'multiText', // TODO: change to multiSelect (based on search filter perhaps) or text search
  dataCategory: 'metadata',
  dataKey: 'slideId',
  label: 'Slides',
  columnWidth: { width: 240 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'id'),
  objectArrayInnerPath: 'id',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
  adminOnly: true,
  renderCell: () => renderSlideIdsCell,
  unwoundRenderCell: () => renderSlideIdCell,
};

export const markersField: DisplayedField<Procedure, any> = {
  filterType: 'multiText',
  dataCategory: 'metadata',
  dataKey: 'channelMarkerTypes' as any,
  label: 'Markers',
  columnWidth: { width: 148 },
  isFieldOfObjectInArray: true,
  objectArrayInnerPath: 'channelMarkerTypes',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
  renderCell: () => renderCaseMarkersCell,
  unwoundRenderCell: () => renderSlideMarkersCell,
  tableViewOnly: true,
};

export const biopsySiteField: DisplayedField<Procedure, (number | string)[], ProceduresFieldsContext> = {
  unwoundRowCellEditType: 'select',
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'biopsySiteId',
  label: 'Biopsy Site',
  columnWidth: { width: 150 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => map(getProcedureSlideFieldValues(procedure, 'biopsySiteId'), (n) => toNumber(n)),
  objectArrayInnerPath: 'biopsySiteId',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  singleObjectFormatter: (value, context) =>
    find(context?.biopsySiteTypes, { id: toNumber(value) })?.displayName || toString(value),
  getNumOfEntries: (procedure) => size(procedure?.slides),
  optionsGetter: (context) =>
    map(context?.biopsySiteTypes, (biopsySiteType) => ({
      value: biopsySiteType.id,
      label: biopsySiteType.displayName,
    })),
  getIsLoading: (context) => Boolean(context?.biopsySiteTypesLoading),
  getError: ({ value, row: _row, context }) => {
    if (context?.biopsySiteTypesLoading) return;
    if (value && !find(context?.biopsySiteTypes, { id: toNumber(value) })) return `Invalid Biopsy Site`;
  },
  enforceUniquePerBaseRow: true,
};

export const stainTypeField: DisplayedField<Procedure, Array<string | null | undefined>, ProceduresFieldsContext> = {
  unwoundRowCellEditType: 'select',
  filterType: 'multiSelect',
  dataKey: 'stainingType',
  label: 'Stain Type(s)',
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'stainingType'),
  singleObjectFormatter: (stainTypeId, context) =>
    (find(context?.stainTypes, { id: stainTypeId }) as StainType)?.displayName || toString(stainTypeId || ''),
  getIsLoading: (context) => Boolean(context?.stainTypesLoading),
  getError: ({ value, row: _row, context }) => {
    if (context?.stainTypesLoading) return;
    if (value && !find(context?.stainTypes, { id: value })) return `Invalid Stain Type`;
    if (context?.isEditPreview && value && !some(context?.stainTypesNotDeprecated, { id: value }))
      return `Deprecated Stain Type`;
  },
  objectArrayInnerPath: 'stainingType',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
  columnWidth: { width: 150 },
  optionsGetter: (context) =>
    map(context?.stainTypesNotDeprecated, (stainType) => ({
      value: stainType.id,
      label: stainType.displayName,
    })),
};

export const biopsyTypeField: DisplayedField<Procedure, Array<string | null | undefined>, ProceduresFieldsContext> = {
  unwoundRowCellEditType: 'select',
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'biopsyType',
  enumType: 'biopsyType',
  label: `Biopsy Type`,
  columnWidth: { width: 150 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'biopsyType'),
  objectArrayInnerPath: 'biopsyType',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  singleObjectFormatter: (value, context) =>
    value ? getDisplayNameFromEnumValue('biopsyType', value, context?.enumDisplayNames) : '',
  getIsLoading: (context) => Boolean(context?.uiSettingsLoading),
  getError: ({ value, row: _row, context }) => {
    if (context?.uiSettingsLoading) return;
    if (value && !find(context?.enumDisplayNames?.biopsyType, { value })) return `Invalid Biopsy Type`;
  },
  enforceUniquePerBaseRow: true,
};

export const procedureTypeField: DisplayedField<
  Procedure,
  Array<string | null | undefined>,
  ProceduresFieldsContext
> = {
  unwoundRowCellEditType: 'select',
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'procedureType',
  enumType: 'procedureType',
  label: 'Procedure Type',
  columnWidth: { width: 150 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'procedureType'),
  objectArrayInnerPath: 'procedureType',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
  singleObjectFormatter: (value, context) =>
    value ? getDisplayNameFromEnumValue('procedureType', value, context?.enumDisplayNames) : '',
  getIsLoading: (context) => Boolean(context?.uiSettingsLoading),
  getError: ({ value, row: _row, context }) => {
    if (context?.uiSettingsLoading) return;
    if (value && !find(context?.enumDisplayNames?.procedureType, { value })) return `Invalid Procedure Type`;
  },
};

export const preservationMethodField: DisplayedField<
  Procedure,
  Array<string | null | undefined>,
  ProceduresFieldsContext
> = {
  unwoundRowCellEditType: 'select',
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'preservationMethod',
  enumType: 'preservationMethod',
  label: 'Preservation Method',
  columnWidth: { width: 160 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'preservationMethod'),
  objectArrayInnerPath: 'preservationMethod',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
  singleObjectFormatter: (value, context) =>
    value ? getDisplayNameFromEnumValue('preservationMethod', value, context?.enumDisplayNames) : '',
  getIsLoading: (context) => Boolean(context?.uiSettingsLoading),
  getError: ({ value, row: _row, context }) => {
    if (context?.uiSettingsLoading) return;
    if (value && !find(context?.enumDisplayNames?.preservationMethod, { value })) return `Invalid Preservation Method`;
  },
};

export const scannerModelField: DisplayedField<Procedure, Array<string | null | undefined>, ProceduresFieldsContext> = {
  unwoundRowCellEditType: 'select',
  filterType: 'multiSelect',
  dataKey: 'scannerModel',
  dataCategory: 'metadata',
  label: 'Scanner Model',
  columnWidth: { width: 150 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'scannerModel'),
  objectArrayInnerPath: 'scannerModel',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  singleObjectFormatter: humanize,
  optionsGetter: (context) =>
    map(context?.scannerModels, (scannerModel) => ({ value: scannerModel, label: scannerModel })),
  tableViewOnly: true,
};

export const scannerManufacturerField: DisplayedField<Procedure, Array<string | null | undefined>> = {
  unwoundRowCellEditType: 'select',
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'scannerManufacturer',
  enumType: 'scannerManufacturer',
  label: 'Scanner Manufacturer',
  columnWidth: { width: 160 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'scannerManufacturer'),
  objectArrayInnerPath: 'scannerManufacturer',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
  singleObjectFormatter: humanize,
  tableViewOnly: true,
};

export const tsmAreaField: DisplayedField<Procedure, Array<number | null | undefined>> = {
  filterType: 'range',
  dataCategory: 'metadata',
  dataKey: 'tsmArea',
  label: 'TSM Area',
  columnWidth: { width: 120 },
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'tsmAreaUm2'),
  isFieldOfObjectInArray: true,
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  objectArrayInnerPath: 'tsmAreaUm2',
  singleObjectFormatter: (value) => (value || value === 0 ? `${formatNumber(value / 1_000_000)} mm²` : ''),
};

export const maxResolutionField: DisplayedField<Procedure, Array<number | null | undefined>> = {
  filterType: 'range',
  dataCategory: 'metadata',
  dataKey: 'maxResolution',
  label: 'Max Resolution',
  columnWidth: { width: 120 },
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'maxResolution'),
  isFieldOfObjectInArray: true,
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  objectArrayInnerPath: 'maxResolution',
  singleObjectFormatter: (value) => (value ? formatSlideResolution(value) : ''),
};

export const negativeControlField: DisplayedField<Procedure, Array<boolean | null | undefined>> = {
  unwoundRowCellEditType: 'select',
  filterType: 'checkbox-group',
  dataKey: 'negativeControl',
  label: 'Negative Control',
  columnWidth: { width: 130 },
  options: [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ] as any[],
  singleObjectFormatter: (option) => (option ? 'Yes' : 'No'),
  isFieldOfObjectInArray: true,
  objectArrayInnerPath: 'negativeControl',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  missingValueOption: false as any,
};

export const metastasisField: DisplayedField<Procedure, Array<boolean | null | undefined>> = {
  unwoundRowCellEditType: 'select',
  filterType: 'checkbox-group',
  dataCategory: 'metadata',
  dataKey: 'metastasis',
  label: 'Metastasis',
  columnWidth: { width: 120 },
  options: [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ] as any[],
  singleObjectFormatter: (option) => (option ? 'Yes' : 'No'),
  isFieldOfObjectInArray: true,
  objectArrayInnerPath: 'metastasis',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  missingValueOption: false as any,
};

export const positiveControlField: DisplayedField<Procedure, Array<boolean | null | undefined>> = {
  unwoundRowCellEditType: 'select',
  filterType: 'checkbox-group',
  dataCategory: 'metadata',
  dataKey: 'positiveControl',
  label: 'Positive Control',
  columnWidth: { width: 130 },
  options: [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ] as any[],
  singleObjectFormatter: (option) => (option ? 'Yes' : 'No'),
  isFieldOfObjectInArray: true,
  objectArrayInnerPath: 'positiveControl',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  missingValueOption: false as any,
};

export const scanDateField: DisplayedField<Procedure, Array<string | null | undefined>> = {
  filterType: 'date-range',
  dataCategory: 'metadata',
  dataKey: 'scanDate',
  label: 'Scan Date',
  isFullDate: true,
  views: ['day', 'month', 'year'],
  columnWidth: { width: 150 },
  singleObjectFormatter(value, _context) {
    return value ? formatDateTimeForUser(value) : 'N/A';
  },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'scanDate'),
  objectArrayInnerPath: 'scanDate',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
};

// The slide table's createdAt field represents the date the slide row was created in our system, which reflects its ingestion date
// rather than the actual scan date
export const ingestionDateField: DisplayedField<Procedure, Array<string | null | undefined>> = {
  filterType: 'date-range',
  dataCategory: 'metadata',
  dataKey: 'ingestionDate',
  label: 'Ingestion Date',
  isFullDate: true,
  views: ['day', 'month', 'year'],
  columnWidth: { width: 150 },
  singleObjectFormatter(value, _context) {
    return value ? formatDateTimeForUser(value) : 'N/A';
  },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'createdAt'),
  objectArrayInnerPath: 'createdAt',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
};

export const qcFailedField: DisplayedField<Procedure, Array<boolean | null | undefined>> = {
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'qcFailed',
  label: 'Failed Quality Control',
  // TODO: add support for boolean options
  options: [
    {
      label: 'Yes',
      value: true,
    },
    {
      label: 'No',
      value: false,
    },
  ] as any[], // Select works with boolean but type isn't supported :(
  columnWidth: { width: 150 },
  isFieldOfObjectInArray: true,
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'qcFailed'),
  objectArrayInnerPath: 'qcFailed',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
  singleObjectFormatter: (qcFailed) => (qcFailed ? 'Yes' : 'No'),
  missingValueOption: false as any,
};

export const numOfChannelsField: DisplayedField<Procedure, Array<number | null | undefined>> = {
  filterType: 'range',
  dataCategory: 'metadata',
  dataKey: 'numOfChannels',
  label: 'Number of Channels',
  columnWidth: { width: 150 },
  valueGetter: (procedure) =>
    map(procedure?.slides, (slide) =>
      slide.stainingType === MULTIPLEX_STAIN_ID ? size(slide?.channelMarkerTypes) : 1
    ),
  isFieldOfObjectInArray: true,
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  objectArrayInnerPath: 'channelMarkerTypes.length',
};

export const objectiveMagnificationField: DisplayedField<
  Procedure,
  Array<number | null | undefined>,
  ProceduresFieldsContext
> = {
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'objectiveMagnification',
  label: 'Objective Magnification',
  getIsLoading: (context) => Boolean(context?.uiSettingsLoading),
  optionsGetter: (context) => context?.enumDisplayNames?.objectiveMagnification || [],
  columnWidth: { width: 180 },
  valueGetter: (procedure) => getProcedureSlideFieldValues(procedure, 'objectiveMagnification'),
  singleObjectFormatter: (value, context) =>
    find(context?.enumDisplayNames?.objectiveMagnification, (option) => Number(option.value) === value)?.label ||
    toString(value),
  isFieldOfObjectInArray: true,
  objectArrayInnerPath: 'objectiveMagnification',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
};

export const encodingOptions: FieldOption[] = [
  {
    label: 'RGB',
    value: 'rgb',
  },
  {
    label: '8-bit',
    value: 'uint8',
  },
  {
    label: '16-bit',
    value: 'uint16',
  },
  {
    label: 'Float',
    value: 'float',
  },
];

export const encodingField: DisplayedField<Procedure, string[]> = {
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'encoding',
  unwoundRowCellEditType: 'select',
  label: 'Encoding',
  options: encodingOptions,
  columnWidth: { width: 150 },
  singleObjectFormatter: (value) => find(encodingOptions, { value })?.label ?? value ?? '8-bit (default)',
  isFieldOfObjectInArray: true,
  objectArrayInnerPath: 'encoding',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
};

export const artifactsField: DisplayedField<Procedure, string[]> = {
  filterType: 'multiSelect',
  dataCategory: 'metadata',
  dataKey: 'artifacts',
  label: 'Excluded Artifacts',
  // Selected values are artifacts to exclude, so there is no missing value to match.
  excludeUnspecifiedFilterOption: true,
  // TODO: get from endpoint
  options: [
    {
      label: 'Folds',
      value: 'Folds',
    },
    {
      label: 'Out of Focus',
      value: 'Out of Focus',
    },
    {
      label: 'Ink Stain',
      value: 'Ink Stain',
    },
  ],
  columnWidth: { width: 150 },
  valueGetter: (procedure) => map(procedure?.slides, (slide) => (slide ? 'None' : '-')),
  // TODO: add artifacts to procedure slides
  isFieldOfObjectInArray: true,
  objectArrayInnerPath: 'artifacts',
  objectArrayKeyPath: 'id',
  objectArrayPath: 'slides',
  getNumOfEntries: (procedure) => size(procedure?.slides),
};

export const slidesMetadataFields: DisplayedField<Procedure, any, any>[] = [
  scanDateField,
  biopsyTypeField,
  procedureTypeField,
  preservationMethodField,
  scannerModelField,
  scannerManufacturerField,
  artifactsField,
  qcFailedField,
  tsmAreaField,
  objectiveMagnificationField,
  maxResolutionField,
  numOfChannelsField,
  encodingField,
];

export const slidesBaseFields: DisplayedField<Procedure, any, any>[] = [
  originalFileNameField,
  formatField,
  slideIdField,
  markersField,
  biopsySiteField,
  stainTypeField,
  metastasisField,
  negativeControlField,
  positiveControlField,
  ingestionDateField,
];

export default concat(slidesBaseFields, slidesMetadataFields);
